import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authFetch } from './api';
import { GoogleArtifactCard } from './CoworkPanel';
import RefineCard from './chat/RefineCard';
import PortfolioCard from './chat/PortfolioCard';
import LearnCard from './chat/LearnCard';
import ExperimentCard from './chat/ExperimentCard';
import NodeFocusCard from './chat/NodeFocusCard';
import DebateCard from './chat/DebateCard';
import GenerationFlowCard from './chat/GenerationFlowCard';
import PrototypeCard from './chat/PrototypeCard';
import PipelineCheckpointCard from './chat/PipelineCheckpointCard';
import EvolutionCard from './chat/EvolutionCard';
import PatternFlowCard from './chat/PatternFlowCard';
import { serializeTree, buildFocusedSubtree } from './treeUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const QUICK_ACTIONS = {
  idea:     [
    { label: 'Gherkin Spec',    prompt: 'Generate a complete Gherkin .feature file from this thinking tree. Write BDD scenarios (Given/When/Then) that cover the core user flows, edge cases, and acceptance criteria for each major feature branch. Group scenarios by Feature and tag them appropriately (@core, @edge-case, @mvp).' },
    { label: 'User Stories',    prompt: 'Extract user stories from this thinking tree. For each story use the format: "As a [persona], I want [goal] so that [benefit]." Include acceptance criteria as a checklist under each story. Prioritize by MoSCoW (Must/Should/Could/Won\'t).' },
    { label: 'Find Blind Spots', prompt: 'Critically analyze this thinking tree for blind spots, missing perspectives, and unstated assumptions. What user segments are ignored? What failure modes aren\'t considered? What competitive threats are missing? What technical risks are unaddressed? Be specific and actionable — for each blind spot, suggest a node or branch that should be added.' },
    { label: 'Sprint Plan',     prompt: 'Break this thinking tree into a sprint plan. Group related nodes into 1-2 week sprints. For each sprint: define the goal, list the deliverables (referencing tree nodes), identify dependencies and blockers, and estimate complexity (S/M/L). Start with an MVP sprint that delivers end-to-end value.' },
  ],
  codebase: [
    { label: 'Gherkin Spec',    prompt: 'Generate Gherkin .feature files for the key behaviors discovered in this codebase analysis. Write BDD scenarios covering the main user-facing flows, API contracts, and critical paths. Tag scenarios by domain area.' },
    { label: 'Tech Debt Audit', prompt: 'Analyze this codebase tree for technical debt. Categorize issues by severity (critical/high/medium/low), estimate remediation effort, and propose a prioritized cleanup backlog. Identify quick wins vs. strategic refactors.' },
    { label: 'Find Blind Spots', prompt: 'What is this codebase analysis missing? Look for: untested paths, missing error handling patterns, security gaps, scalability bottlenecks, missing observability, and undocumented assumptions. Be specific about where in the architecture each gap exists.' },
    { label: 'Migration Plan',  prompt: 'Create a phased migration or refactoring plan from this codebase analysis. Define clear milestones, rollback strategies, and feature flags needed. Each phase should be independently deployable and testable.' },
  ],
  resume:   [
    { label: 'User Stories',    prompt: 'Reframe this resume analysis as user stories from the hiring manager\'s perspective. "As a hiring manager, I want to see [evidence of X] so that I can confirm [capability Y]." Map each story to specific resume content that satisfies it.' },
    { label: 'Find Blind Spots', prompt: 'What gaps exist in this resume strategy? Look for: missing keywords from the JD, unexploited transferable skills, weak quantification, missing social proof, and positioning blind spots. For each gap, suggest specific content to add.' },
    { label: 'Interview Prep',  prompt: 'Create interview preparation notes based on this tree — likely questions, STAR stories to prepare, and key talking points. Focus on areas where the resume is weakest.' },
    { label: 'Sprint Plan',     prompt: 'Break this resume improvement strategy into a weekly action plan. Week 1: quick wins (formatting, keywords). Week 2: content upgrades (quantification, achievements). Week 3: positioning (summary, narrative). Week 4: distribution (LinkedIn, networking).' },
  ],
  decision: [
    { label: 'Gherkin Spec',    prompt: 'Generate Gherkin scenarios that test this decision. Write Given/When/Then for: each option\'s success criteria, risk triggers that would change the recommendation, and validation experiments to run before committing. This turns the decision into testable hypotheses.' },
    { label: 'User Stories',    prompt: 'Extract stakeholder stories from this decision tree. "As a [stakeholder], I need [outcome] so that [value]." Map conflicting needs across stakeholders and identify where trade-offs must be made.' },
    { label: 'Find Blind Spots', prompt: 'What is this decision analysis missing? Look for: second-order effects, reversibility assumptions, timeline risks, stakeholder perspectives not represented, opportunity costs of each option, and scenarios where the "obvious" choice fails. Be provocative.' },
    { label: 'Sprint Plan',     prompt: 'Create an execution plan for the recommended decision. Break it into phases: validation (experiments to de-risk), commitment (point of no return), execution (implementation steps), and measurement (success criteria checkpoints).' },
  ],
  writing:  [
    { label: 'Gherkin Spec',    prompt: 'Generate Gherkin scenarios that define "done" for this writing project. Write Given/When/Then for: reader comprehension goals, argument completeness, evidence quality, and structural coherence. These become your editing checklist.' },
    { label: 'User Stories',    prompt: 'Extract reader stories from this writing tree. "As a [reader persona], I want to [understand/feel/do X] so that [outcome]." Map each section of the writing to the reader needs it serves.' },
    { label: 'Find Blind Spots', prompt: 'What is this writing plan missing? Look for: counterarguments not addressed, evidence gaps, logical leaps, audience segments ignored, missing transitions between sections, and claims that need citation. Suggest specific additions.' },
    { label: 'Sprint Plan',     prompt: 'Break this writing project into a daily writing plan. Each day: define the section to draft, word count target, research needed, and key arguments to make. Include revision and feedback cycles.' },
  ],
  plan:     [
    { label: 'Gherkin Spec',    prompt: 'Generate Gherkin acceptance scenarios for each milestone in this plan. Write Given/When/Then that define when each phase is truly complete — not just "code done" but validated, tested, and deployed. Tag by priority and phase.' },
    { label: 'User Stories',    prompt: 'Extract user stories from this plan tree. For each deliverable, write stories that define the user-facing value. Group by milestone and identify stories that span multiple phases (epics).' },
    { label: 'Find Blind Spots', prompt: 'What risks and gaps exist in this plan? Look for: missing dependencies, optimistic timeline assumptions, single points of failure, unassigned ownership, missing rollback plans, and phases that depend on external factors outside your control. Suggest mitigations.' },
    { label: 'Sprint Plan',     prompt: 'Convert this plan into sprint-sized work packages. For each sprint: define a clear demo-able outcome, list specific tasks with effort estimates, identify blockers to resolve upfront, and define the "definition of done." Flag the critical path.' },
  ],
  learn:    [
    { label: 'Start Learning',    prompt: '__START_LEARN__' },
    { label: 'Find Blind Spots', prompt: 'What gaps exist in my understanding of this topic? Analyze the concept tree and identify: prerequisites I may be weak on, common misconceptions in this area, advanced topics I haven\'t explored, and connections to other fields I\'m missing. Prioritize what to study next.' },
    { label: 'Quiz Me',          prompt: 'Create a challenging quiz based on this learning tree. Include: 3 conceptual questions (test understanding, not recall), 2 application questions (use the concept in a new context), and 1 synthesis question (connect multiple concepts). Explain the correct answers after I attempt them.' },
    { label: 'Study Plan',       prompt: 'Break this learning tree into a structured study plan. For each concept: define the learning objective, suggest resources (papers, tutorials, exercises), estimate time needed, and define how I\'ll know I\'ve mastered it. Order by prerequisite dependencies.' },
  ],
};

// ── Markdown Renderer ─────────────────────────────────────────
export function ChatMarkdown({ content }) {
  const components = useMemo(() => ({
    // Code blocks with copy button + language badge
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const lang = match ? match[1] : '';
      const codeText = String(children).replace(/\n$/, '');

      if (!inline && (lang || codeText.includes('\n'))) {
        return (
          <div className="chat-code-block">
            {lang && <span className="chat-code-lang">{lang}</span>}
            <button
              className="chat-code-copy"
              onClick={() => navigator.clipboard?.writeText(codeText)}
              title="Copy code"
            >⧉</button>
            <pre><code className={className} {...props}>{children}</code></pre>
          </div>
        );
      }
      return <code className="chat-inline-code" {...props}>{children}</code>;
    },
    // Open links in new tab
    a({ href, children, ...props }) {
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
    // Tables get wrapper for horizontal scroll
    table({ children, ...props }) {
      return (
        <div className="chat-table-wrap">
          <table {...props}>{children}</table>
        </div>
      );
    },
  }), []);

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}

// serializeTree and buildFocusedSubtree imported from treeUtils.js

const ACTION_DELIMITER = '<<<ACTIONS>>>';

// Compaction: keep last N messages verbatim, summarize older ones
const COMPACT_THRESHOLD = 16; // start compacting at 16+ messages
const KEEP_RECENT = 10;       // always keep the most recent 10 messages
function compactMessages(messages) {
  if (messages.length <= COMPACT_THRESHOLD) return messages;
  const older = messages.slice(0, messages.length - KEEP_RECENT);
  const recent = messages.slice(messages.length - KEEP_RECENT);
  // Summarize older messages into a single context message
  const summary = older.map(m => {
    const label = m.role === 'user' ? 'User' : 'Assistant';
    const text = (m.content || '').slice(0, 200);
    return `[${label}]: ${text}`;
  }).join('\n');
  return [
    { role: 'user', content: `[CONVERSATION HISTORY SUMMARY — ${older.length} earlier messages compacted]\n${summary}\n[END SUMMARY — Recent messages follow]` },
    { role: 'assistant', content: 'Understood, I have the conversation context from the summary above. Continuing from the recent messages.' },
    ...recent,
  ];
}

const ACTION_LABELS = {
  filter: 'Filtered Graph',
  clear: 'Cleared Filters',
  addNodes: 'Added Nodes',
  debate: 'Started Debate',
  refine: 'Started Refine',
  portfolio: 'Generating Portfolio',
  fractalExpand: 'Fractal Expanding',
  scoreNodes: 'Scoring Nodes',
  drill: 'Drilling Into Node',
  feedToIdea: 'Bridging to Idea Mode',
  executeAction: 'Executing Fix',
  refineMore: 'Continuing Refine',
  portfolioMore: 'More Alternatives',
  expandNode: 'Expanding Node',
  editNode: 'Edited Node',
  regenerateNode: 'Regenerating Node',
  starNode: 'Toggled Star',
  deleteNode: 'Deleted Node',
  deleteBranch: 'Deleted Branch',
  executePattern: 'Running Pattern',
  buildPrototype: 'Building Prototype',
};

function parseActions(fullText) {
  // Find <<<ACTIONS>>> — may be preceded by ``` code fence
  const idx = fullText.indexOf(ACTION_DELIMITER);
  if (idx === -1) return { displayText: fullText, actions: null };

  // Strip display text: remove any trailing ``` or ```json before the delimiter
  let displayText = fullText.slice(0, idx);
  displayText = displayText.replace(/```(?:json)?\s*$/, '').trimEnd();

  // Extract JSON after delimiter, strip any trailing ``` code fence
  let actionJson = fullText.slice(idx + ACTION_DELIMITER.length).trim();
  actionJson = actionJson.replace(/```\s*$/, '').trim();

  try {
    const actions = JSON.parse(actionJson);
    return { displayText, actions };
  } catch (e) {
    // Try to extract JSON object from the string (AI may add extra text)
    const jsonMatch = actionJson.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const actions = JSON.parse(jsonMatch[0]);
        return { displayText, actions };
      } catch (_) { /* fall through */ }
    }
    console.warn('Failed to parse chat actions:', e, actionJson);
    return { displayText, actions: null };
  }
}

const CHAT_MODE_CONFIG = {
  idea:     { title: 'PRODUCT STRATEGIST', icon: '✦', emptyDesc: 'Your thinking tree is loaded as context. Ask questions or use a quick action below to generate actionable outputs.' },
  codebase: { title: 'TECH ADVISOR',       icon: '⟨/⟩', emptyDesc: 'Your codebase analysis is loaded as context. Ask questions or use a quick action below to generate technical docs.' },
  resume:   { title: 'CAREER COACH',       icon: '◎', emptyDesc: 'Your resume strategy tree is loaded as context. Ask questions or use a quick action below to generate career materials.' },
  decision: { title: 'DECISION ANALYST',   icon: '⚖', emptyDesc: 'Your decision tree is loaded as context. Ask questions or use a quick action below to generate decision docs.' },
  writing:  { title: 'WRITING EDITOR',     icon: '✦', emptyDesc: 'Your writing analysis is loaded as context. Ask questions or use a quick action below to generate content.' },
  plan:     { title: 'PROJECT ADVISOR',    icon: '◉', emptyDesc: 'Your project plan is loaded as context. Ask questions or use a quick action below to generate project docs.' },
  learn:    { title: 'AI TUTOR',            icon: '⧫', emptyDesc: 'Your concept tree is ready. Click "Start Learning" below — I\'ll teach each concept with explanations and examples, then quiz you to check understanding.' },
};

export default function ChatPanel({ isOpen, onClose, nodes, idea, mode = 'idea', onChatAction, chatFilterActive, onClearFilter, pendingChatCards, onClearPendingCards, onCardButtonClick, executionStream, onStopExecution, onDismissStream, refineStream, portfolioStream, learnStream, experimentStream, debateStream, prototypeStream, emailContext, pipelineStages, onClosePipeline, focusedNode, onDismissFocus, patternFramework, patternExecState, onStopPattern, availablePatterns, sessionFileContext, pipelineCheckpoint, onPipelineCheckpointAction, mnemonicJobs, onGenerateVideo, onPlayVideo, initialChatMessages, onSaveChatMessages, generationFlow, sessionId, workflowOutput, googleArtifacts, onClickArtifact, onChatExploreDeeper, chaosLevel }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [chatAttachedFiles, setChatAttachedFiles] = useState([]);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const chatFileRef = useRef(null);
  const execStreamRef = useRef(null);
  const initializedRef = useRef(false);
  // Track which live card markers have been inserted to avoid duplicates
  const insertedMarkersRef = useRef(new Set());

  // Load saved chat messages on mount
  useEffect(() => {
    if (initialChatMessages?.length && !initializedRef.current) {
      initializedRef.current = true;
      messageCountAtLoadRef.current = initialChatMessages.length;
      setMessages(initialChatMessages);
    }
  }, [initialChatMessages]);

  // Save messages when they change (debounced)
  // Skip saving when messages are just loaded from initialChatMessages — only save on NEW activity
  const messageCountAtLoadRef = useRef(0);
  useEffect(() => {
    if (!onSaveChatMessages || messages.length === 0) return;
    // Don't save if messages haven't changed since initial load
    if (messages.length <= messageCountAtLoadRef.current) return;
    const timer = setTimeout(() => {
      // Only save user/assistant messages (not action cards or live markers)
      const saveable = messages.filter(m => m.role === 'user' || m.role === 'assistant');
      if (saveable.length > 0) onSaveChatMessages(saveable);
    }, 1000);
    return () => clearTimeout(timer);
  }, [messages, onSaveChatMessages]);

  // Auto-scroll on new messages or streaming text (not on every card prop update)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  // Auto-scroll execution stream output to bottom
  useEffect(() => {
    if (execStreamRef.current) {
      execStreamRef.current.scrollTop = execStreamRef.current.scrollHeight;
    }
  }, [executionStream]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Reset when idea changes
  useEffect(() => {
    setMessages([]);
    setStreamingText('');
    setInput('');
    initializedRef.current = false;
  }, [idea]);

  // Consume pending action cards from parent
  useEffect(() => {
    if (pendingChatCards?.length > 0) {
      setMessages(prev => [...prev, ...pendingChatCards.map(card => ({
        role: 'system',
        type: 'action_card',
        ...card,
      }))]);
      onClearPendingCards?.();
    }
  }, [pendingChatCards, onClearPendingCards]);

  // ── Inject live card markers into messages when status props appear ──
  // Each marker is inserted once when the prop transitions from falsy to truthy.
  // The marker stays in the message list so the card appears at its chronological position.
  const injectMarker = useCallback((type) => {
    if (insertedMarkersRef.current.has(type)) return;
    insertedMarkersRef.current.add(type);
    setMessages(prev => [...prev, { role: 'system', type, ts: Date.now() }]);
  }, []);

  // Clear marker tracking when the prop goes away (card dismissed/completed)
  useEffect(() => {
    if (!pipelineStages) insertedMarkersRef.current.delete('pipeline_live');
  }, [pipelineStages]);
  useEffect(() => {
    if (!generationFlow?.length) insertedMarkersRef.current.delete('generation_flow_live');
  }, [generationFlow]);
  useEffect(() => {
    if (!executionStream) insertedMarkersRef.current.delete('execution_stream_live');
  }, [executionStream]);
  useEffect(() => {
    if (!debateStream) insertedMarkersRef.current.delete('debate_live');
  }, [debateStream]);
  useEffect(() => {
    if (!refineStream) insertedMarkersRef.current.delete('refine_live');
  }, [refineStream]);
  useEffect(() => {
    if (!portfolioStream) insertedMarkersRef.current.delete('portfolio_live');
  }, [portfolioStream]);
  useEffect(() => {
    if (!learnStream) insertedMarkersRef.current.delete('learn_live');
  }, [learnStream]);
  useEffect(() => {
    if (!experimentStream) insertedMarkersRef.current.delete('experiment_live');
  }, [experimentStream]);
  useEffect(() => {
    if (!prototypeStream) insertedMarkersRef.current.delete('prototype_live');
  }, [prototypeStream]);
  useEffect(() => {
    if (!patternExecState) insertedMarkersRef.current.delete('pattern_live');
  }, [patternExecState]);
  useEffect(() => {
    if (!pipelineCheckpoint) insertedMarkersRef.current.delete('pipeline_checkpoint_live');
  }, [pipelineCheckpoint]);

  // Inject markers when live cards start
  useEffect(() => { if (pipelineStages) injectMarker('pipeline_live'); }, [pipelineStages, injectMarker]);
  useEffect(() => { if (generationFlow?.length) injectMarker('generation_flow_live'); }, [generationFlow, injectMarker]);
  useEffect(() => { if (executionStream) injectMarker('execution_stream_live'); }, [executionStream, injectMarker]);
  useEffect(() => { if (debateStream) injectMarker('debate_live'); }, [debateStream, injectMarker]);
  useEffect(() => { if (refineStream) injectMarker('refine_live'); }, [refineStream, injectMarker]);
  useEffect(() => { if (portfolioStream) injectMarker('portfolio_live'); }, [portfolioStream, injectMarker]);
  useEffect(() => { if (learnStream) injectMarker('learn_live'); }, [learnStream, injectMarker]);
  useEffect(() => { if (experimentStream) injectMarker('experiment_live'); }, [experimentStream, injectMarker]);
  useEffect(() => { if (prototypeStream) injectMarker('prototype_live'); }, [prototypeStream, injectMarker]);
  useEffect(() => { if (patternExecState) injectMarker('pattern_live'); }, [patternExecState, injectMarker]);
  useEffect(() => { if (pipelineCheckpoint) injectMarker('pipeline_checkpoint_live'); }, [pipelineCheckpoint, injectMarker]);

  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isStreaming) return;

    // Append attached file content to the user message
    let fullContent = content.trim();
    if (chatAttachedFiles.length > 0) {
      const fileBlock = chatAttachedFiles.map(f => `[Attached: ${f.name}]\n${f.text}`).join('\n\n');
      fullContent += `\n\n--- ATTACHED FILES ---\n${fileBlock}`;
      setChatAttachedFiles([]); // clear after sending
    }
    const userMsg = { role: 'user', content: fullContent };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);
    setStreamingText('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const focusContext = buildFocusedSubtree(focusedNode, nodes);
      // When a node is focused, send focused subtree as primary context
      // and full tree as secondary background — this ensures the AI
      // prioritizes the selected node instead of treating the full tree equally.
      const treeContext = focusContext
        ? serializeTree(nodes.filter(n => !focusContext.subtree.includes(n.id)))
        : serializeTree(nodes);
      const res = await authFetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: compactMessages(newMessages.filter(m => m.role === 'user' || m.role === 'assistant')),
          treeContext,
          idea,
          mode,
          emailThread: emailContext?.formatted || null,
          focusedNode: focusContext,
          sessionFileContext: sessionFileContext || null,
          sessionId: sessionId || null,
          chaosLevel: chaosLevel ?? 100,
          googleArtifacts: googleArtifacts?.length ? googleArtifacts.map(ga => ({
            id: ga.id, type: ga.type, title: ga.title, url: ga.url || ga.editUrl, createdAt: ga.createdAt,
          })) : null,
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let fullText = '';
      let actionsDispatchedEarly = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) {
              fullText += parsed.text;
              setStreamingText(fullText);
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }

        // Eagerly dispatch actions as soon as <<<ACTIONS>>> JSON is complete,
        // so export loading cards appear while the stream is still open.
        if (!actionsDispatchedEarly && fullText.includes(ACTION_DELIMITER)) {
          const { actions: earlyActions } = parseActions(fullText);
          if (earlyActions && onChatAction) {
            onChatAction(earlyActions);
            actionsDispatchedEarly = true;
          }
        }
      }

      const { displayText, actions } = parseActions(fullText);
      // Build list of executed action names for visual indicator
      const executedActions = actions ? Object.keys(actions).filter(k =>
        ['filter', 'clear', 'addNodes', 'debate', 'refine', 'portfolio',
         'fractalExpand', 'scoreNodes', 'drill', 'feedToIdea', 'refineMore', 'portfolioMore'].includes(k) && actions[k]
      ) : [];
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: displayText,
        executedActions: executedActions.length ? executedActions : undefined,
      }]);
      setStreamingText('');
      // Only dispatch if we didn't already fire during streaming
      if (!actionsDispatchedEarly && actions && onChatAction) onChatAction(actions);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      setStreamingText('');
    } finally {
      setIsStreaming(false);
    }
  }, [messages, nodes, idea, mode, isStreaming, onChatAction, focusedNode, emailContext?.formatted]);

  const handleStop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    if (streamingText) {
      const { displayText } = parseActions(streamingText);
      setMessages(prev => [...prev, { role: 'assistant', content: displayText }]);
      setStreamingText('');
    }
    setIsStreaming(false);
  }, [streamingText]);

  // File attachment handler — reads files and extracts text client-side
  const handleChatFileAttach = useCallback((e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    const readers = fileList.map(file => {
      return new Promise((resolve) => {
        // For text-based files, read content. For others, just store name.
        const textTypes = ['.txt', '.md', '.json', '.yaml', '.yml', '.js', '.ts', '.jsx', '.tsx', '.py', '.go', '.rs', '.rb', '.csv', '.sql', '.sh'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (textTypes.includes(ext) && file.size < 100000) {
          const reader = new FileReader();
          reader.onload = (ev) => resolve({ name: file.name, size: file.size, text: ev.target.result?.slice(0, 50000) });
          reader.readAsText(file);
        } else {
          resolve({ name: file.name, size: file.size, text: `[Binary file: ${file.name} (${(file.size / 1024).toFixed(1)}KB)]` });
        }
      });
    });
    Promise.all(readers).then(files => {
      setChatAttachedFiles(prev => [...prev, ...files]);
    });
    e.target.value = ''; // reset input
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }, [input, sendMessage]);

  const handleCopy = useCallback((text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        // Fallback for non-HTTPS / unfocused contexts
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }, []);

  if (!isOpen) return null;

  const actions = patternFramework?.quickActions?.length > 0
    ? patternFramework.quickActions
    : (QUICK_ACTIONS[mode] || QUICK_ACTIONS.idea);
  const hasTree = nodes && nodes.length > 0;

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <div className="chat-panel-title">
          <span className="chat-panel-icon">{(CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).icon}</span>
          <span>{(CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).title}</span>
          {hasTree && (
            <span className="chat-node-count">{nodes.length} nodes</span>
          )}
          {chatFilterActive && (
            <button className="chat-filter-badge" onClick={onClearFilter}>
              FILTERED ✕
            </button>
          )}
        </div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>

      {/* Messages area */}
      <div className="chat-messages" ref={scrollRef}>
        {messages.length === 0 && !streamingText && (
          <div className="chat-empty">
            <div className="chat-empty-icon">{(CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).icon}</div>
            <div className="chat-empty-title">{(CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).title}</div>
            <div className="chat-empty-desc">
              {hasTree
                ? (CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).emptyDesc
                : 'Generate a thinking tree first, then use the companion to create actionable outputs from it.'}
            </div>
            {hasTree && (
              <div className="chat-quick-actions">
                {actions.map((a) => (
                  <button
                    key={a.label}
                    className={`chat-quick-btn${a.prompt === '__START_LEARN__' ? ' chat-quick-btn-primary' : ''}`}
                    onClick={() => a.prompt === '__START_LEARN__' ? onCardButtonClick?.({ actionType: 'openPanel', panel: 'learn' }) : sendMessage(a.prompt)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          // ── Live card markers — render the live card component at its chronological position ──
          msg.type === 'pipeline_live' ? (
            pipelineStages ? (() => {
              const activeStage = pipelineStages.find(s => s.status === 'active');
              const allDone = pipelineStages.every(s => s.status === 'done');
              const completedCount = pipelineStages.filter(s => s.status === 'done').length;
              const totalCount = pipelineStages.length;
              const ICONS = { generate: '◈', debate: '⚔', refine: '⟲', portfolio: '◆' };
              return (
                <div key={i} className={`pipeline-chat-card ${allDone ? 'pipeline-chat-done' : ''}`}>
                  <div className="pipeline-chat-header">
                    <span className="pipeline-chat-icon">⟡</span>
                    <span className="pipeline-chat-title">
                      {allDone ? 'PIPELINE COMPLETE' : 'PIPELINE ACTIVE'}
                    </span>
                    <span className="pipeline-chat-counter">{completedCount}/{totalCount}</span>
                    <button className="pipeline-chat-close" onClick={onClosePipeline}>✕</button>
                  </div>
                  <div className="pipeline-chat-stepper">
                    {pipelineStages.map((stage, si) => (
                      <React.Fragment key={stage.id}>
                        <div className={`pipeline-chat-stage ${
                          stage.status === 'done' ? 'pcs-done' :
                          stage.status === 'active' ? 'pcs-active' : 'pcs-pending'
                        }`}>
                          <div className="pcs-icon">
                            {stage.status === 'done' ? '✓' : ICONS[stage.id] || '●'}
                          </div>
                          <div className="pcs-label">{stage.label}</div>
                        </div>
                        {si < pipelineStages.length - 1 && (
                          <div className={`pcs-connector ${stage.status === 'done' ? 'pcs-connector-done' : ''}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  {activeStage && (
                    <div className="pipeline-chat-detail">
                      <span className="pipeline-pulse">●</span>
                      {activeStage.detail || activeStage.label}
                      {activeStage.round && (
                        <span className="pipeline-chat-round"> — Round {activeStage.round}/{activeStage.maxRounds}</span>
                      )}
                    </div>
                  )}
                  {activeStage?.substages && (
                    <div className="pipeline-chat-substages">
                      {activeStage.substages.map((sub, si) => (
                        <span key={si} className={`pcs-sub ${
                          sub.status === 'done' ? 'sub-done' :
                          sub.status === 'active' ? 'sub-active' : 'sub-pending'
                        }`}>
                          {sub.status === 'done' ? '✓' : sub.status === 'active' ? '●' : '○'} {sub.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {allDone && (
                    <div className="pipeline-chat-done-msg">All stages complete ✓</div>
                  )}
                </div>
              );
            })() : null
          ) : msg.type === 'generation_flow_live' ? (
            generationFlow?.length > 0 ? (
              <GenerationFlowCard
                key={i}
                stages={generationFlow}
                isComplete={generationFlow.every(s => s.status === 'done' || s.status === 'pending')}
                nodeCount={generationFlow.find(s => s.id === 'tree-gen')?.nodeCount || 0}
              />
            ) : null
          ) : msg.type === 'execution_stream_live' ? (
            executionStream ? (
              <div key={i} className={`exec-stream-card ${executionStream.done ? (executionStream.error ? 'exec-stream-error' : 'exec-stream-done') : 'exec-stream-live'}`}>
                <div className="exec-stream-header">
                  <span className="exec-stream-icon">{executionStream.done ? (executionStream.error ? '✗' : '✓') : '⟳'}</span>
                  <span className="exec-stream-title">
                    {executionStream.done
                      ? (executionStream.error ? 'Fix failed' : 'Fix completed')
                      : `Fixing: ${executionStream.nodeLabel}`}
                  </span>
                  {!executionStream.done && (
                    <button className="exec-stream-stop" onClick={onStopExecution} title="Stop execution">⏹ Stop</button>
                  )}
                  {executionStream.done && (
                    <button className="exec-stream-dismiss" onClick={onDismissStream} title="Dismiss">✕</button>
                  )}
                </div>
                <div className="exec-stream-body" ref={execStreamRef}>
                  <pre className="exec-stream-output">{executionStream.text || 'Starting Claude Code…'}{!executionStream.done && <span className="chat-cursor">▊</span>}</pre>
                </div>
              </div>
            ) : null
          ) : msg.type === 'debate_live' ? (
            debateStream ? <DebateCard key={i} state={debateStream} onAction={onCardButtonClick} /> : null
          ) : msg.type === 'refine_live' ? (
            refineStream ? <RefineCard key={i} state={refineStream} onAction={onCardButtonClick} /> : null
          ) : msg.type === 'portfolio_live' ? (
            portfolioStream ? <PortfolioCard key={i} state={portfolioStream} onAction={onCardButtonClick} /> : null
          ) : msg.type === 'learn_live' ? (
            learnStream ? <LearnCard key={i} state={learnStream} onAction={onCardButtonClick} mnemonicJob={null} /> : null
          ) : msg.type === 'experiment_live' ? (
            experimentStream ? <ExperimentCard key={i} state={experimentStream} onAction={onCardButtonClick} /> : null
          ) : msg.type === 'prototype_live' ? (
            prototypeStream ? <PrototypeCard key={i} state={prototypeStream} onAction={onCardButtonClick} /> : null
          ) : msg.type === 'pattern_live' ? (
            patternExecState ? (
              <PatternFlowCard
                key={i}
                patternName={patternExecState.patternName || 'PATTERN'}
                stages={patternExecState.stages || [
                  { id: 'exec', label: patternExecState.stage || 'Executing...', status: 'active', round: patternExecState.round, startedAt: patternExecState.startedAt },
                ]}
                isComplete={patternExecState.complete}
                isError={patternExecState.error}
                onStop={onStopPattern}
              />
            ) : null
          ) : msg.type === 'pipeline_checkpoint_live' ? (
            pipelineCheckpoint ? <PipelineCheckpointCard key={i} state={pipelineCheckpoint} onAction={onPipelineCheckpointAction} /> : null
          // ── Static card messages (from saved sessions) ──
          ) : msg.type === 'refine_card' ? (
            <RefineCard key={i} state={msg.state} onAction={onCardButtonClick} />
          ) : msg.type === 'portfolio_card' ? (
            <PortfolioCard key={i} state={msg.state} onAction={onCardButtonClick} />
          ) : msg.type === 'experiment_card' ? (
            <ExperimentCard key={i} state={msg.state} onAction={onCardButtonClick} />
          ) : msg.type === 'debate_card' ? (
            <DebateCard key={i} state={msg.state} onAction={onCardButtonClick} />
          ) : msg.type === 'pipeline_checkpoint' ? (
            <PipelineCheckpointCard key={i} state={msg.checkpoint} onAction={onPipelineCheckpointAction} />
          ) : msg.type === 'evolution' ? (
            <EvolutionCard key={i} taskId={msg.taskId} plan={msg.plan} evolutionHistory={msg.evolutionHistory} />
          ) : msg.type === 'action_card' ? (
            <div key={i} className="chat-action-card">
              <div className="chat-action-card-header">
                <span className="chat-action-card-icon">⚡</span>
                <span className="chat-action-card-label">{msg.label}</span>
              </div>
              {msg.detail && <div className="chat-action-card-detail">{msg.detail}</div>}
              {msg.buttons?.length > 0 && (
                <div className="chat-action-card-buttons">
                  {msg.buttons.map((btn, j) => (
                    <button
                      key={j}
                      className="chat-action-card-btn"
                      onClick={() => onCardButtonClick?.(btn)}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div key={i} className={`chat-msg chat-msg-${msg.role}`}>
              <div className="chat-msg-label">
                {msg.role === 'user' ? 'YOU' : msg.role === 'system' ? '⏰ TASK' : 'AI'}
              </div>
              <div className="chat-msg-content">
                {msg.role === 'assistant'
                  ? <ChatMarkdown content={msg.content} />
                  : msg.content}
              </div>
              {msg.executedActions?.length > 0 && (
                <div className="chat-tool-badges">
                  {msg.executedActions.map(action => (
                    <span key={action} className="chat-tool-badge">
                      ⚡ {ACTION_LABELS[action] || action}
                    </span>
                  ))}
                </div>
              )}
              {/* Google Workspace artifact cards — shown after messages that created them */}
              {msg.executedActions?.some(a => a.startsWith('exportToGoogle')) && googleArtifacts?.length > 0 && (
                <div className="chat-artifact-cards">
                  {googleArtifacts
                    .filter(ga => {
                      // Show artifacts created around this message's timestamp
                      if (!msg.timestamp || !ga.createdAt) return false;
                      const msgTime = new Date(msg.timestamp).getTime();
                      const artTime = new Date(ga.createdAt).getTime();
                      return Math.abs(artTime - msgTime) < 30000; // within 30s
                    })
                    .map(ga => (
                      <GoogleArtifactCard key={ga.id} artifact={ga} onClickArtifact={onClickArtifact} />
                    ))
                  }
                </div>
              )}
              {msg.role === 'assistant' && (
                <div className="chat-msg-actions">
                  <button
                    className="chat-copy-btn"
                    onClick={() => handleCopy(msg.content)}
                    title="Copy to clipboard"
                  >
                    ⧉ Copy
                  </button>
                  {mode === 'learn' && onChatExploreDeeper && (
                    <button
                      className="chat-copy-btn chat-explore-deeper-btn"
                      onClick={() => {
                        // Find the preceding user message to use as the drill topic
                        const msgIdx = messages.indexOf(msg);
                        const prevUser = [...messages].slice(0, msgIdx).reverse().find(m => m.role === 'user');
                        const topic = prevUser?.content?.split('\n')[0]?.slice(0, 120) || 'Follow-up topic';
                        onChatExploreDeeper(topic, msg.content);
                      }}
                      title="Drill deeper into this topic as a learning sub-tree"
                    >
                      ⧫ Explore Deeper
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        ))}

        {/* ── Streaming AI response (always at bottom, after all messages) ── */}
        {streamingText && (() => {
          let visibleStream = streamingText.indexOf(ACTION_DELIMITER) > -1
            ? streamingText.slice(0, streamingText.indexOf(ACTION_DELIMITER))
            : streamingText;
          // Strip any trailing code fence before the delimiter
          visibleStream = visibleStream.replace(/```(?:json)?\s*$/, '').trimEnd();
          return visibleStream ? (
            <div className="chat-msg chat-msg-assistant">
              <div className="chat-msg-label">AI</div>
              <div className="chat-msg-content chat-msg-streaming">
                <ChatMarkdown content={visibleStream} />
                <span className="chat-cursor">▊</span>
              </div>
            </div>
          ) : null;
        })()}

        {/* ── Google Workspace artifacts (persistent at bottom of chat) ── */}
        {googleArtifacts?.length > 0 && (
          <div className="chat-google-artifacts-section">
            <div className="chat-google-artifacts-label">Created artifacts</div>
            {googleArtifacts.map(ga => (
              <GoogleArtifactCard key={ga.id} artifact={ga} onClickArtifact={onClickArtifact} />
            ))}
          </div>
        )}

        {/* ── Node Focus Card (always at bottom — contextual, not chronological) ── */}
        {focusedNode && (
          <NodeFocusCard
            node={focusedNode.node}
            surgicalExpanded={focusedNode.surgicalExpanded}
            isSplitting={focusedNode.isSplitting}
            isMerging={focusedNode.isMerging}
            mergeTarget={focusedNode.mergeTarget}
            onAction={onCardButtonClick}
            onDismiss={onDismissFocus}
            availablePatterns={availablePatterns}
          />
        )}

        {/* ── Workflow Output Document (persistent, updates in-place) ── */}
        {workflowOutput && (
          <div className="chat-msg chat-msg-assistant">
            <div className="chat-msg-label">Output</div>
            <div className="chat-msg-content">
              <ChatMarkdown content={workflowOutput} />
            </div>
          </div>
        )}
      </div>

      {/* Quick actions — shown after first exchange too */}
      {messages.length > 0 && hasTree && (
        <div className="chat-quick-bar">
          {chatFilterActive && (
            <button
              className="chat-quick-chip chat-clear-chip"
              onClick={onClearFilter}
              disabled={isStreaming}
            >
              ✕ Clear Filters
            </button>
          )}
          {actions.map((a) => (
            <button
              key={a.label}
              className="chat-quick-chip"
              onClick={() => sendMessage(a.prompt)}
              disabled={isStreaming}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Attached files preview */}
      {chatAttachedFiles.length > 0 && (
        <div className="chat-attached-files">
          {chatAttachedFiles.map((f, i) => (
            <span key={i} className="chat-attached-chip">
              📎 {f.name} <button className="chat-attached-x" onClick={() => setChatAttachedFiles(prev => prev.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="chat-input-area">
        <button
          className="chat-attach-btn"
          onClick={() => chatFileRef.current?.click()}
          title="Attach files"
          disabled={isStreaming}
        >
          📎
        </button>
        <input
          ref={chatFileRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.txt,.md,.json,.yaml,.yml,.js,.ts,.jsx,.tsx,.py,.go,.rs,.rb"
          style={{ display: 'none' }}
          onChange={handleChatFileAttach}
        />
        <textarea
          ref={inputRef}
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={hasTree ? 'Ask anything about your tree...' : 'Generate a tree first...'}
          disabled={isStreaming || !hasTree}
          rows={1}
        />
        {isStreaming ? (
          <button className="chat-send-btn chat-stop-btn" onClick={handleStop}>■</button>
        ) : (
          <button
            className="chat-send-btn"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || !hasTree}
          >
            ▶
          </button>
        )}
      </div>
    </div>
  );
}
